import { readGhanaianLanguages } from "@shared/ghanaianLanguages";
import React, { createContext, useContext, useEffect, useState, ReactNode, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from './AuthContext';
import { UserPreferences, UserPreferencesContextType, DEFAULT_PREFERENCES, KIDS_GRADE_LEVELS, type GradeLevel } from '@/types/userPreferences';
import { userPreferencesStorage, gradeLevelStorage } from '@/lib/localStorage';
import themes from '@/themes/themes';
import { applyPalette, loadSignedOutAppearance, resolvePalette } from '@/themes/appearance';
import { Theme } from '@/types/themes';
import { rememberAccountWalkthroughs } from '@/components/walkthrough/walkthroughHistory';
import { readStudyPreferences } from '@/lib/studyPreferences';

// Exported for the DEV-only quiz harness (src/dev/QuizHarness.tsx), which
// supplies signed-out preferences. App code uses useUserPreferences().
export const UserPreferencesContext = createContext<UserPreferencesContextType | undefined>(undefined);

interface UserPreferencesProviderProps {
  children: ReactNode;
}

export const UserPreferencesProvider: React.FC<UserPreferencesProviderProps> = ({ children }) => {
  const { user, authReady } = useAuth();

  const [preferences, setPreferences] = useState<UserPreferences | null>(() => {
    return userPreferencesStorage.load();
  });

  const [loadedForUserId, setLoadedForUserId] = useState<string>();
  const activeUserId = useRef(user?.id);
  activeUserId.current = user?.id;

  const [isThemeLoading, setIsThemeLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(() => {
    // If we have cached preferences, don't show a blocking loading state
    return !userPreferencesStorage.load();
  });
  const isLoadingRef = useRef(isLoading);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  useEffect(() => {
    const savedPrefs = userPreferencesStorage.load();
    const deviceAppearance = loadSignedOutAppearance();
    // Cached account choices always win. A first visit uses the public Folio
    // theme; persisted account preferences arrive before protected routes render.
    let initialMode = deviceAppearance?.mode || 'light';

    // A kids-mode account has its own, separate dark-mode system
    // (KidsDarkModeContext's `novari_kids_dark_mode` key, defaulting to
    // light) that KidsLoadingScreen and every kids page read directly —
    // this effect applying the ADULT `theme_mode` preference's dark class
    // to <html> here ran with zero awareness of that, so a kids account
    // whose cached theme_mode happened to be "dark" got <html class="dark">
    // applied immediately on load, before anything downstream had a chance
    // to react — NovariLoadingScreen (which paints from that class via
    // --bg-primary) would flash dark, then KidsLoadingScreen would replace
    // it with its own correctly-light background once KidsRouteGuard's own
    // cached-grade check caught up. Using the same gradeLevelStorage cache
    // KidsRouteGuard/AppShell already rely on for this exact race avoids
    // ever applying the adult theme here for a kids account at all — kids
    // dark mode is applied later, separately, by KidsDarkModeProvider once
    // it mounts. Found 2026-09-08 auditing Kids Mode polish.
    let cachedIsKids = false;
    try {
      const lastGrade = gradeLevelStorage.load();
      cachedIsKids = lastGrade ? KIDS_GRADE_LEVELS.includes(lastGrade as GradeLevel) : false;
    } catch { /* ignore */ }

    try {
      if (cachedIsKids) {
        initialMode = localStorage.getItem('novari_kids_dark_mode') === 'true' ? 'dark' : 'light';
      } else if (savedPrefs?.theme_mode) {
        initialMode = savedPrefs.theme_mode as 'light' | 'dark';
      }
      // No saved choice means a first visit: stay light like the landing and
      // sign-in pages. Following the OS here made a dark-mode device go
      // light landing -> dark sign-in -> dark loading screen.
    } catch (e) {
      console.error('[UserPreferencesContext] Failed to load initial theme preferences:', e);
      initialMode = 'light';
    }

    const root = document.documentElement;
    if (!root.hasAttribute('data-landing-theme')) {
    root.classList.remove('light', 'dark');
    root.classList.add(initialMode);
    root.setAttribute('data-theme', initialMode);
    // Pin color-scheme to exactly the active mode (not "light dark") so the
    // browser's own forced-dark heuristics never second-guess a page we
    // already theme ourselves — see the html{} rule in index.css for why.
    root.style.colorScheme = initialMode;
    applyPalette(cachedIsKids ? 'paper' : savedPrefs ? resolvePalette(savedPrefs.ui_theme) : deviceAppearance?.palette || 'folio');
    }

    const timer = setTimeout(() => setIsThemeLoading(false), 50);
    return () => clearTimeout(timer);
  }, []);

  const fetchPreferences = async () => {
    const startTime = Date.now();
    if (!user?.id || !authReady) {
      if (authReady) setIsLoading(false);
      return;
    }

    const ownerId = user.id;
    try {
      // Only show loading if we don't have ANY preferences yet
      if (!preferences) setIsLoading(true);
      setError(null);

      const { data, error: fetchError, status } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('auth_user_id', ownerId)
        .maybeSingle();

      if (activeUserId.current !== user.id) return;
      if (fetchError) {
        if (status === 401 || status === 403 || fetchError.code === 'PGRST301') {
          setError(fetchError.message || "Failed to fetch preferences");
          if (!preferences) {
            setPreferences(DEFAULT_PREFERENCES);
            userPreferencesStorage.save(DEFAULT_PREFERENCES);
          }
          return;
        }

        if (fetchError.message?.includes('multiple') || fetchError.message?.includes('no rows')) {
          const defaults = DEFAULT_PREFERENCES;
          setPreferences(defaults);
          userPreferencesStorage.save(defaults);
        } else {
          throw fetchError;
        }
      } else if (!data) {
        const defaults = DEFAULT_PREFERENCES;
        setPreferences(defaults);
        userPreferencesStorage.save(defaults);
      } else {
        const preferencesFromView: UserPreferences = {
          onboarding_completed: data.onboarding_completed ?? DEFAULT_PREFERENCES.onboarding_completed,
          completed_at: data.completed_at ?? DEFAULT_PREFERENCES.completed_at,
          grade_level:
            (typeof data.grade_level === "string" && data.grade_level.length > 0
              ? data.grade_level
              : undefined) ??
            (data.preferences &&
            typeof data.preferences === "object" &&
            !Array.isArray(data.preferences) &&
            typeof data.preferences.gradeLevel === "string" &&
            data.preferences.gradeLevel.length > 0
              ? data.preferences.gradeLevel
              : undefined),
          user_type: data.user_type ?? DEFAULT_PREFERENCES.user_type,
          education_level: data.education_level ?? DEFAULT_PREFERENCES.education_level,
          work_area: data.work_area ?? DEFAULT_PREFERENCES.work_area,
          learning_intents: data.learning_intents ?? DEFAULT_PREFERENCES.learning_intents,
          gamification_enabled: data.gamification_enabled ?? DEFAULT_PREFERENCES.gamification_enabled,
          explanation_complexity: data.explanation_complexity ?? DEFAULT_PREFERENCES.explanation_complexity,
          layout: data.layout ?? DEFAULT_PREFERENCES.layout,
          feature_emphasis: data.feature_emphasis ?? DEFAULT_PREFERENCES.feature_emphasis,
          system_prompt: data.system_prompt ?? DEFAULT_PREFERENCES.system_prompt,
          welcome_message: data.welcome_message ?? DEFAULT_PREFERENCES.welcome_message,
          ui_theme: data.ui_theme ?? DEFAULT_PREFERENCES.ui_theme,
          theme_mode: data.theme_mode ?? DEFAULT_PREFERENCES.theme_mode,
          avatar_seed: data.avatar_seed ?? DEFAULT_PREFERENCES.avatar_seed,
          subjects: data.subjects ?? DEFAULT_PREFERENCES.subjects,
          ghanaian_languages: readGhanaianLanguages(data.preferences?.ghanaian_languages),
          country: data.country ?? DEFAULT_PREFERENCES.country,
          target_exam: data.target_exam ?? DEFAULT_PREFERENCES.target_exam,
          // timestamptz in the view; the exam-date picker works in whole days.
          target_date: typeof data.target_date === 'string' && data.target_date ? data.target_date.slice(0, 10) : undefined,
          currency: data.currency ?? DEFAULT_PREFERENCES.currency,
          preferred_language: data.preferred_language ?? DEFAULT_PREFERENCES.preferred_language,
          sidebar_collapsed: data.sidebar_collapsed ?? DEFAULT_PREFERENCES.sidebar_collapsed,
          tutorial_completed: data.tutorial_completed ?? DEFAULT_PREFERENCES.tutorial_completed,
          quiz_layout: (data.quiz_layout as UserPreferences['quiz_layout']) ?? (data.preferences?.quiz_layout as UserPreferences['quiz_layout']) ?? DEFAULT_PREFERENCES.quiz_layout,
          // JSONB-only key (the view has no column); read like programme/grade below.
          quiz_question_types: Array.isArray(data.preferences?.quiz_question_types) ? (data.preferences.quiz_question_types as string[]) : null,
          walkthroughs_seen: Array.isArray(data.preferences?.walkthroughs_seen)
            ? data.preferences.walkthroughs_seen.filter((id: unknown): id is string => typeof id === 'string') : [],
          scheduling_onboarded: data.scheduling_onboarded ?? (data.preferences?.scheduling_onboarded) ?? false,
          // scheduling_type comes back as a string from the view; cast to the union type
          scheduling_type: (data.scheduling_type as 'daily' | 'weekly' | undefined) ?? (data.preferences?.scheduling_type as 'daily' | 'weekly' | undefined) ?? undefined,
          daily_study_hours: typeof data.daily_study_hours === 'number' ? data.daily_study_hours : (typeof data.preferences?.daily_study_hours === 'number' ? data.preferences.daily_study_hours : 2),
          // weekly_schedule is stored as JSONB; the view returns it as an object
          weekly_schedule: (data.weekly_schedule && typeof data.weekly_schedule === 'object' && !Array.isArray(data.weekly_schedule))
            ? (data.weekly_schedule as Record<string, { start: string; end: string }[]>)
            : ((data.preferences?.weekly_schedule && typeof data.preferences.weekly_schedule === 'object' && !Array.isArray(data.preferences.weekly_schedule))
               ? (data.preferences.weekly_schedule as Record<string, { start: string; end: string }[]>) : {}),
          study_strategy: (data.study_strategy as 'strict' | 'balanced' | undefined) ?? (data.preferences?.study_strategy as 'strict' | 'balanced' | undefined) ?? 'balanced',
          study_goal: data.study_goal ?? undefined,
          // Onboarding writes programme/grade into the users.preferences JSONB
          // (the RPC merges p_preferences into it); the view has no columns for
          // them, so they were never read back and preferences.programme was
          // always undefined on the client.
          programme: typeof data.preferences?.programme === "string" ? data.preferences.programme : undefined,
          grade: typeof data.preferences?.grade === "string" ? data.preferences.grade : undefined,
          // Backed by user_settings via the view's LEFT JOIN (added
          // alongside this notifications block) — previously the view never
          // exposed these columns at all, so this object was always
          // undefined and every Notifications toggle silently fell back to
          // its hardcoded default on load, looking unsaved even though the
          // RPC had written it correctly.
          notifications: {
            ...DEFAULT_PREFERENCES.notifications!,
            intensity: (data.notification_intensity as NonNullable<UserPreferences['notifications']>['intensity']) ?? DEFAULT_PREFERENCES.notifications!.intensity,
            streak_reminders: data.notification_streak_reminders ?? DEFAULT_PREFERENCES.notifications!.streak_reminders,
            goal_countdowns: data.notification_goal_countdowns ?? DEFAULT_PREFERENCES.notifications!.goal_countdowns,
            smart_nudges: data.notification_smart_nudges ?? DEFAULT_PREFERENCES.notifications!.smart_nudges,
            preferred_times: data.notification_preferred_times ?? DEFAULT_PREFERENCES.notifications!.preferred_times,
            quiet_hours_enabled: data.notification_quiet_hours_enabled ?? DEFAULT_PREFERENCES.notifications!.quiet_hours_enabled,
            quiet_hours_start: data.notification_quiet_hours_start ?? DEFAULT_PREFERENCES.notifications!.quiet_hours_start,
            quiet_hours_end: data.notification_quiet_hours_end ?? DEFAULT_PREFERENCES.notifications!.quiet_hours_end,
          },
        };
        Object.assign(preferencesFromView, readStudyPreferences(data));
        preferencesFromView.gradeLevel = preferencesFromView.grade_level;
        setPreferences(preferencesFromView);
        userPreferencesStorage.save(preferencesFromView);
      }
      if (!fetchError) setLoadedForUserId(user.id);
    } catch (err) {
      if (activeUserId.current !== ownerId) return;
      setError(err instanceof Error ? err.message : 'Failed to fetch preferences');
      if (!preferences) setPreferences(DEFAULT_PREFERENCES);
    } finally {
      if (activeUserId.current === ownerId) setIsLoading(false);
    }
  };

  /** Formats a Supabase PostgrestError (or any error) into a readable string. */
  const formatSupabaseError = (err: unknown): string => {
    if (!err) return 'Unknown error';
    if (err instanceof Error) return err.message;
    // PostgrestError has message, details, hint, code
    const e = err as Record<string, unknown>;
    const parts: string[] = [];
    if (e.message) parts.push(String(e.message));
    if (e.code)    parts.push(`code=${e.code}`);
    if (e.details) parts.push(`details=${e.details}`);
    if (e.hint)    parts.push(`hint=${e.hint}`);
    return parts.length ? parts.join(' | ') : JSON.stringify(err);
  };

  // Latest preferences, readable from async callbacks without a stale closure.
  const preferencesRef = useRef(preferences);
  preferencesRef.current = preferences;
  // Send scoped patches so another device's dismissal or settings cannot be
  // overwritten by an unrelated change from this tab's cached preferences.
  const writeChainRef = useRef<Promise<unknown>>(Promise.resolve());

  const updatePreferences = async (updates: Partial<UserPreferences>) => {
    if (!user?.id) return;

    const ownerId = user.id;
    const previousPreferences = preferencesRef.current;
    const newPreferences = { ...(previousPreferences || DEFAULT_PREFERENCES), ...updates };
    setError(null);
    preferencesRef.current = newPreferences;
    setPreferences(newPreferences);
    userPreferencesStorage.save(newPreferences);

    const write = writeChainRef.current.catch(() => undefined).then(async () => {
      if (activeUserId.current !== ownerId) return;
      const { error: updateError } = await supabase.rpc('update_user_profile_preferences', {
        p_user_id: ownerId,
        p_preferences: updates,
        p_full_name: user.full_name || null
      });
      if (updateError) {
        const msg = formatSupabaseError(updateError);
        console.error('[UserPreferencesContext] DB update failed:', msg, updateError);
        throw updateError;
      }
    });
    writeChainRef.current = write;

    try {
      await write;
    } catch (err) {
      if (activeUserId.current !== ownerId) return;
      const msg = formatSupabaseError(err);
      console.error('[UserPreferencesContext] Rollback preferences due to error:', msg);
      // Only undo this change's own keys, so later successful edits survive.
      const current = preferencesRef.current;
      const rolledBack = current && previousPreferences
        ? { ...current, ...Object.fromEntries(Object.keys(updates).filter(k => Object.is((current as unknown as Record<string, unknown>)[k], (newPreferences as unknown as Record<string, unknown>)[k])).map((k) => [k, (previousPreferences as unknown as Record<string, unknown>)[k]])) } as UserPreferences
        : previousPreferences;
      preferencesRef.current = rolledBack;
      setPreferences(rolledBack);
      if (rolledBack) {
        userPreferencesStorage.save(rolledBack);
      } else {
        userPreferencesStorage.clear();
      }
      setError(err instanceof Error ? err.message : msg);
      throw err;
    }
  };

  useEffect(() => {
    const resetTours = () => {
      if (!user?.id || loadedForUserId !== user.id) return;
      const retained = (preferencesRef.current?.walkthroughs_seen || []).filter(id => id.startsWith('tester:'));
      rememberAccountWalkthroughs(user.id, retained);
      void updatePreferences({ walkthroughs_seen: retained, walkthroughs_reset: true }).catch(() => undefined);
    };
    window.addEventListener('novari:replay-tips', resetTours);
    return () => window.removeEventListener('novari:replay-tips', resetTours);
    // updatePreferences reads the latest state from preferencesRef.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, loadedForUserId]);

  const patchLocalPreferences = (updates: Partial<UserPreferences>) => {
    setPreferences((prev) => {
      const next = { ...(prev || DEFAULT_PREFERENCES), ...updates };
      userPreferencesStorage.save(next);
      return next;
    });
  };

  const updateUiTheme = async (theme: string) => {
    await updatePreferences({ ui_theme: theme });
  };

  const updateThemeMode = async (mode: 'light' | 'dark') => {
    await updatePreferences({ theme_mode: mode });
  };

  const toggleSidebarCollapsed = async () => {
    const currentState = preferences?.sidebar_collapsed ?? false;
    await updatePreferences({ sidebar_collapsed: !currentState });
  };

  useEffect(() => {
    if (!preferences) return;
    // A kids-mode account's dark/light mode is owned entirely by
    // KidsDarkModeProvider (its own `novari_kids_dark_mode` key, defaulting
    // to light) — this effect applying the ADULT theme_mode preference's
    // class here regardless used to fight it: any preferences refetch while
    // a kids page was open (e.g. background revalidation) re-ran this and
    // silently stomped KidsDarkModeProvider's class back to the ambient
    // adult theme, undoing a kid's (or the loading-screen fix's) light
    // theme with no visible trigger. Bailing out here for kids accounts and
    // leaving <html> alone lets KidsDarkModeProvider be the single owner of
    // this class while any kids page is mounted. Found 2026-09-08 auditing
    // Kids Mode polish.
    const gradeLevel = preferences.grade_level ?? preferences.gradeLevel;
    const isKidsAccount = gradeLevel ? KIDS_GRADE_LEVELS.includes(gradeLevel as GradeLevel) : false;
    if (isKidsAccount) return;

    const mode = preferences.theme_mode || 'light';
    // Account palette and mode are independent, synchronized preferences.
    const htmlElement = document.documentElement;
    if (htmlElement.hasAttribute("data-landing-theme")) return;
    htmlElement.classList.remove('light', 'dark');
    htmlElement.classList.add(mode);
    htmlElement.setAttribute('data-theme', mode);
    htmlElement.style.colorScheme = mode;
    applyPalette(resolvePalette(preferences.ui_theme));

    // Colour, radius and elevation tokens for both Paper (light) and
    // Lamplight (dark) live in index.css and key off the class set above.
    // Clear any inline custom properties an older build (or a cached
    // session) wrote here so they can't shadow the stylesheet.
    for (let i = htmlElement.style.length - 1; i >= 0; i--) {
      const prop = htmlElement.style.item(i);
      if (prop.startsWith('--')) htmlElement.style.removeProperty(prop);
    }
  }, [preferences?.theme_mode, preferences?.ui_theme]);

  useEffect(() => {
    if (authReady) fetchPreferences();
  }, [user?.id, authReady]);

  useEffect(() => {
    if (!authReady) return;
    const safetyTimeout = setTimeout(() => {
      if (isLoadingRef.current) setIsLoading(false);
    }, 10000);
    return () => clearTimeout(safetyTimeout);
  }, [authReady]);

  const currentTheme = preferences?.ui_theme && preferences.ui_theme !== ''
    ? (themes[preferences.ui_theme as keyof typeof themes]?.dark || null)
    : null;

  const value: UserPreferencesContextType = React.useMemo(() => ({
    preferences,
    isLoading,
    isThemeLoading,
    error,
    loadedForUserId: loadedForUserId === user?.id ? loadedForUserId : undefined,
    refetch: fetchPreferences,
    updatePreferences,
    patchLocalPreferences,
    currentTheme,
    updateUiTheme,
    updateThemeMode,
    toggleSidebarCollapsed,
  }), [
    preferences,
    isLoading,
    isThemeLoading,
    error,
    currentTheme,
    loadedForUserId,
    user?.id
  ]);

  return (
    <UserPreferencesContext.Provider value={value}>
      {children}
    </UserPreferencesContext.Provider>
  );
};

export const useUserPreferences = (): UserPreferencesContextType => {
  const context = useContext(UserPreferencesContext);
  if (context === undefined) throw new Error('useUserPreferences must be used within a UserPreferencesProvider');
  return context;
};
